import type React from 'react';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@xing-com/button';
import { ProgressBar } from '@xing-com/progress-bar';
import { space6XL, spaceL } from '@xing-com/tokens';
import { BodyCopy } from '@xing-com/typography';

import { useSearchContext } from '../../context';
import { useLimitedSearchCount, useQueryParamsListener } from '../../hooks';

const Container = styled.div`
  margin-bottom: ${space6XL};
  margin-top: ${spaceL};
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  gap: ${spaceL};
`;

export const LazyLoader: React.FC = () => {
  const { searchPageSize, loadMore, count, listLength, loading } =
    useSearchContext();

  const [page, setPage] = useState(() => {
    const length = listLength ?? searchPageSize;
    return Math.ceil(length / searchPageSize);
  });
  const { formatMessage } = useIntl();
  const offset = searchPageSize * page;
  const total = useLimitedSearchCount();

  const resetPagination = (): void => {
    setPage(1);
  };

  useQueryParamsListener(resetPagination);

  if (count <= offset) {
    return null;
  }

  return (
    <Container>
      <div>
        <BodyCopy>
          {formatMessage(
            { id: 'SE_PAGINATION_TEXT' },
            { results: offset, total }
          )}
        </BodyCopy>
        <ProgressBar progress={(offset / count) * 100} showLabel={false} />
      </div>
      <Button
        onClick={() => {
          loadMore();
          setPage(page + 1);
        }}
        variant="secondary"
        loading={loading}
      >
        {formatMessage({ id: 'SE_PAGINATION_SHOW_MORE' })}
      </Button>
    </Container>
  );
};
